import { useEffect, useState } from "react";
import { X, ChevronRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

/**
 * One-time onboarding card overlay shown on the Board the first time a
 * caregiver opens a profile (`profile.onboarded === false`). Three steps,
 * dismissable. Persisted via the profile model so it stays dismissed
 * across devices.
 */

const STEPS = [
  {
    title: "Tap to speak",
    body: "Tap any tile and the device speaks the word out loud. Tap a few in a row to build a sentence in the strip up top.",
    art: "👆",
  },
  {
    title: "Build the board for your child",
    body: "Tap Edit (top right) → add tiles, attach a real photo of Grandma or the snack box, and record your own voice. Personal photos and voices help kids learn faster.",
    art: "🎙️",
  },
  {
    title: "Lock it down",
    body: "Open Settings to set a caregiver PIN. Edit mode and Settings will lock — kids tap freely without changing the layout. Drag-to-reorder is parent-only.",
    art: "🔒",
  },
];

export default function OnboardingTour({ profileId, profile, onDone }) {
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!profile) return;
    if (profile.onboarded) return;
    setOpen(true);
  }, [profile]);

  if (!open || !profile) return null;

  const finish = async () => {
    setOpen(false);
    try {
      // Persist via PUT — only need to flip onboarded but the model needs full body.
      await api.put(`/profiles/${profileId}`, {
        name: profile.name,
        avatar: profile.avatar,
        color: profile.color,
        voice_rate: profile.voice_rate,
        voice_pitch: profile.voice_pitch,
        voice_name: profile.voice_name || null,
        tile_size: profile.tile_size,
        ai_suggestions: profile.ai_suggestions !== false,
        language: profile.language || "en",
        first_word_celebrated: !!profile.first_word_celebrated,
        dwell_ms: parseInt(profile.dwell_ms || 0, 10),
        scan_mode: !!profile.scan_mode,
        scan_interval_ms: parseInt(profile.scan_interval_ms || 1500, 10),
        onboarded: true,
      });
    } catch (err) {
      // Offline or transient — flag will retry on next visit.
      // Logging keeps this debuggable if the PUT body is wrong.
      console.warn("OnboardingTour: failed to persist onboarded flag", err?.response?.status || err?.message);
    }
    onDone?.();
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else finish();
  };

  const cur = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <div className="modal-overlay" data-testid="onboarding-tour">
      <div className="brutal-card p-6 w-full max-w-md text-center bg-white">
        <div className="flex items-start justify-between mb-2">
          <span className="text-xs uppercase tracking-[0.2em] text-[color:var(--ink-soft)] flex items-center gap-1">
            <Sparkles size={12} className="text-[color:var(--gold)]" />
            Welcome
          </span>
          <button
            type="button"
            className="pill-btn"
            onClick={finish}
            aria-label="Skip tour"
            data-testid="onboarding-skip"
          >
            <X size={14} /> Skip
          </button>
        </div>

        <div className="text-6xl my-3" aria-hidden>{cur.art}</div>
        <h2 className="display text-2xl font-bold mb-2">{cur.title}</h2>
        <p className="text-[color:var(--ink-soft)] mb-5">{cur.body}</p>

        <div className="flex items-center justify-center gap-1.5 mb-4">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`w-2 h-2 rounded-full border-[2px] border-[color:var(--ink)] ${i === step ? "bg-[color:var(--coral)]" : "bg-white"}`}
            />
          ))}
        </div>

        <button
          type="button"
          className="pill-btn primary w-full"
          onClick={next}
          data-testid="onboarding-next"
        >
          {last ? "Start using the board" : "Got it"}
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
